import React from 'react'
import { useApolloClient } from 'react-apollo'

import { Button, Modal, ModalInjectedProps } from '@sketch/components'

import {
  CloudBillingPlanFragment,
  useUndoScheduledSubscriptionCancellationMutation,
} from '@sketch/gql-types'

import { dateFormat, formatPriceWithCurrency } from '@sketch/utils'
import {
  getPlanDuration,
  getPlanName,
  updateSubscriptionEndInCache,
} from '../../utils'

// This is a valid use case to narrow down Component type
// eslint-disable-next-line no-restricted-imports
import { Currency } from '@sketch/gql-types/expansive'

export interface ReactivateSubscriptionModalProps extends ModalInjectedProps {
  customerId: string
  customerCurrency: Currency
  currentPlan: CloudBillingPlanFragment
  subscriptionEnd: string
  workspaceId: string
  isPartner?: boolean
}

export const ReactivateSubscriptionModal: React.FC<
  ReactivateSubscriptionModalProps
> = props => {
  const {
    hideModal,
    customerId,
    currentPlan,
    customerCurrency,
    subscriptionEnd,
    workspaceId,
    isPartner,
  } = props
  const client = useApolloClient()

  const [reactivate, { data, loading }] =
    useUndoScheduledSubscriptionCancellationMutation({
      variables: { customerId },
      onError: 'show-toast',
      onCompleted: data => {
        if (!data?.undoScheduledSubscriptionCancellation?.customer) {
          return
        }

        const { subscriptionEnd } =
          data?.undoScheduledSubscriptionCancellation.customer
            .subscriptionInfo || {}

        updateSubscriptionEndInCache(client, {
          workspaceId,
          subscriptionEnd,
          isPartner,
        })
      },
    })

  let content = null

  const planName = getPlanName(currentPlan.type)
  const planInterval = getPlanDuration(currentPlan.type)
  const renewalDate = dateFormat(new Date(subscriptionEnd))

  const price = currentPlan.prices.find(
    price => price.currency === customerCurrency
  )

  if (data?.undoScheduledSubscriptionCancellation?.customer) {
    content = (
      <>
        <Modal.Header>{planName} plan reactivated</Modal.Header>
        <Modal.Body>
          <Modal.Text>
            Your <b>{planName.toLowerCase()} subscription</b> has been
            reactivated.{' '}
            <b>
              {price && formatPriceWithCurrency(price.invoice, price.currency)}/
              {planInterval} per Editor
            </b>{' '}
            will be charged on your next renewal date, <b>{renewalDate}</b>.
            <br /> <br />
            The total cost will be based on the number of Editors on your
            workspace at the time of billing.
          </Modal.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={hideModal}>Close</Button>
        </Modal.Footer>
      </>
    )
  } else {
    content = (
      <>
        <Modal.Header>
          Reactivate your {planName.toLowerCase()} plan?
        </Modal.Header>
        <Modal.Body>
          <Modal.Text>
            Your <b>{planName.toLowerCase()} subscription</b> will be
            reactivated and we’ll charge you{' '}
            <b>
              {price && formatPriceWithCurrency(price.invoice, price.currency)}/
              {planInterval} per Editor
            </b>{' '}
            on your workspace. You won’t be charged until your next renewal date
            on <b>{renewalDate}</b>.
          </Modal.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={loading} onClick={hideModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={loading}
            onClick={() => reactivate()}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </>
    )
  }

  return <Modal onCancel={hideModal}>{content}</Modal>
}
